import { useLocation, useNavigate } from "@solidjs/router";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import type {
  EndCheck,
  ParkingResponse,
  PaymentMethodView,
  Position,
  TripSummary,
  TripView,
} from "../../shared/api";
import { TARIF_BLEU_LABEL } from "../../shared/tariffs";
import { nearestParkings, rideDistanceLabel, zoneStatus } from "../../shared/parking";
import { rateOf } from "../../shared/rate";
import { ApiFailure, api } from "../api";
import { keepOnFailedRefresh } from "../resource";
import { Button } from "../components/Button";
import { ComingSoonToast, comingSoon } from "../components/ComingSoon";
import { MenuIcon } from "../components/icons";
import { ListRow } from "../components/ListRow";
import { ItineraryBanner } from "../components/ItineraryBanner";
import { MAP_TOP, MapTopRow } from "../components/MapTopRow";
import { MenuDrawer } from "../components/MenuDrawer";
import { ParkingCheckModal } from "../components/ParkingCheckModal";
import { Modal } from "../components/Modal";
import { Sheet } from "../components/Sheet";
import { SheetBand } from "../components/SheetBand";
import { LoadingPill } from "../components/Spinner";
import { SlideToConfirm } from "../components/SlideToConfirm";
import { Toggle } from "../components/Toggle";
import { euros, holdMethodLabel, mmss } from "../format";
import { ParkingPhoto } from "./ParkingPhoto";
import { createMapView, type MapView } from "../map/view";
import { distanceMeters, freshPosition, geo, watchPosition } from "../state/geo";
import { reportHref } from "../report";
import { autoSafetyTutorial, markStorySeen, type StorySet, storySeen } from "../stories";
import { DuoChoiceSheet, type RideMode } from "../components/DuoChoiceSheet";
import { Stories } from "../components/Stories";
import { ZoneSheet } from "../components/ZoneSheet";
import { ZoneToast } from "../components/ZoneToast";
import {
  type ZoneSheet as ZoneSheetData,
  type ZonesHere,
  zoneSheetAt,
  zonesAt,
  zonesEntered,
} from "../../shared/zones";
import { rideFailure } from "../rideFailure";
import { rideFrameKey } from "../rideFrame";
import { bannerFor } from "../itinerary";
import { createRoutePath } from "../state/routePath";
import { tripPlan } from "../state/tripPlan";

const POLL_MS = 10_000;

const REFUSALS: Record<string, string> = {
  FORBIDDEN_PARKING: "Le stationnement est interdit ici.",
  STRICT_PARKING: "Stationnement obligatoire sur un emplacement dédié.",
  JAUGE_FAILED_STRICT: "Ce parking est plein.",
};

/** Figma E4 body (the penalty amount is left out on purpose). */
const PROOF_TEXT =
  "Notre système indique que vous n'êtes pas stationné dans une zone. Si c'est une erreur, prenez une photo pour prouver votre bon stationnement. En cas de stationnement irrégulier, vous vous exposez à une pénalité.";

/** "Stationnement Comédie" → "Comédie" for the map callout and the nearby cards (E3). */
const shortName = (name: string) => name.replace(/^Stationnement\s+/i, "");

/**
 * Figma 05b Bike unlocked → 06 Ride in progress (E1 outside a zone, E3 guidance, E4 proof),
 * P1 pause confirmation → P2 paused, plus the end-of-ride verdict states (PLAN.md D8).
 */
export function Ride() {
  const navigate = useNavigate();
  const location = useLocation<{ unlocked?: string }>();
  // Polled every 10 s: one failed refresh (Pony hiccup, lost network) keeps the ride on screen
  // instead of erroring the resource, which broke the screen and stopped the polling.
  const [trip, { refetch, mutate }] = createResource<TripView | null>(
    keepOnFailedRefresh(async () => {
      try {
        return await api.get<TripView>("/trips/current");
      } catch (e) {
        if (e instanceof ApiFailure && e.code === "NO_ONGOING_TRIP") return null;
        throw e;
      }
    }),
  );
  // Zones for the live hint (band, route, guidance). Optional: without them the ride works as before.
  const [parking] = createResource(
    () =>
      api
        .get<ParkingResponse>("/parking")
        .catch((): ParkingResponse => ({ parking: [], forbidden: [] })),
    { initialValue: { parking: [], forbidden: [], zones: [] } as ParkingResponse },
  );
  const [unlockedSheet, setUnlockedSheet] = createSignal(!!location.state?.unlocked);
  const [duo, setDuo] = createSignal(false);
  const [menu, setMenu] = createSignal(false);
  /** Onboarding stories open (Figma S1–S6, Z1–Z3) and the solo / duo choice (U1/U2). */
  const [stories, setStories] = createSignal<StorySet>();
  const [duoChoice, setDuoChoice] = createSignal(false);
  // First ride: the safety tutorial opens by itself on the start sheet, once (seen when finished
  // or closed, so it doesn't come back every ride).
  let safetyShown = false;
  createEffect(() => {
    if (
      !autoSafetyTutorial({
        startSheet: unlockedSheet() && !!trip(),
        seen: storySeen("safety"),
        shownThisRide: safetyShown,
      })
    )
      return;
    safetyShown = true;
    markStorySeen("safety");
    setStories("safety");
  });
  const chooseMode = (mode: RideMode) => {
    setDuoChoice(false);
    setDuo(mode === "duo"); // display only: no Pony call (decision 14/10/2026)
    // The first time duo is chosen: how to ride with a passenger (S4–S6).
    if (mode === "duo" && !storySeen("duo")) setStories("duo");
  };
  /** Figma 06b (285:14509): the 05b sheet reopened from "Infos du trajet". */
  const [infoSheet, setInfoSheet] = createSignal(false);
  const [method] = createResource(() =>
    api.get<PaymentMethodView | null>("/payments/method").catch(() => null),
  );
  const [elapsed, setElapsed] = createSignal(0);
  const [busy, setBusy] = createSignal<"pause" | "resume" | "end" | undefined>();
  const [notice, setNotice] = createSignal<{
    kind: "warning" | "refusal" | "error";
    text: string;
    retryAfter?: number;
  }>();
  const [warning, setWarning] = createSignal<EndCheck>();
  /** E2 "Oups" (before E4): the end check said outside a zone and a parking is nearby. */
  const [oops, setOops] = createSignal<EndCheck>();
  /** P1 open. */
  const [askPause, setAskPause] = createSignal(false);
  /** E3 open, on this parking id. */
  const [guide, setGuide] = createSignal<string>();
  /** Parking chosen with "Y aller": the route stays drawn until the bike is in a zone. */
  const [target, setTarget] = createSignal<string>();
  let container!: HTMLDivElement;
  const [view, setView] = createSignal<MapView>();

  watchPosition();

  // Trip ended elsewhere (callback / Pony) or none: leave.
  createEffect(() => {
    if (!trip.loading && trip() === null) navigate("/map", { replace: true });
  });

  onMount(async () => {
    setView(
      await createMapView({
        container,
        center: geo.position(),
        // Figma K2–K5 on the ride map too: a tap on a zone opens its sheet over the ride sheet.
        onSelect: (sel) =>
          setZoneSheet(
            sel?.kind === "zone" && sel.at
              ? zoneSheetAt(sel.at, parking().zones ?? [], vehicleType())
              : undefined,
          ),
      }),
    );
  });
  const tick = setInterval(() => {
    const t = trip();
    if (t) setElapsed(Math.max(0, Math.round((Date.now() - Date.parse(t.startedAt)) / 1000)));
  }, 1000);
  const poll = setInterval(() => {
    if (document.visibilityState === "visible" && !busy()) void refetch();
  }, POLL_MS);
  onCleanup(() => {
    clearInterval(tick);
    clearInterval(poll);
    view()?.destroy();
  });

  const paused = () => trip()?.state === "PAUSED";
  /** km/h from the phone's GPS; re-read every second (the clock tick) so a lost fix drops to 0. */
  const speedNow = () => {
    elapsed();
    return paused() ? 0 : geo.speed();
  };
  /** Where the bike is: GBFS when Pony still lists it, else the rider's phone (Pony drops bikes in a trip). */
  const bike = () => trip()?.vehicle ?? (geo.located() ? geo.position() : undefined);
  /** Scooters' ids start with "S" at Pony; speeds and rules may differ by type. */
  const vehicleType = () =>
    (trip()?.vehicleId ?? "").toUpperCase().startsWith("S")
      ? ("scooter" as const)
      : ("ebike" as const);
  const zonesHere = createMemo(() => {
    const b = bike();
    return b ? zonesAt(b, parking().zones ?? [], vehicleType()) : undefined;
  });
  const status = createMemo(() => {
    const b = bike();
    // Off the operating zone the ride can't end (Figma K5): as a forbidden spot (E1 band).
    if (zonesHere()?.outside) return { kind: "forbidden" } as const;
    return b ? zoneStatus(b, parking()) : ({ kind: "unknown" } as const);
  });
  // Figma K2–K5 sheet opened by a tap on a zone, and the toast when the bike enters one.
  const [zoneSheet, setZoneSheet] = createSignal<ZoneSheetData>();
  const [zoneToast, setZoneToast] = createSignal<ZoneSheetData>();
  let lastHere: ZonesHere | undefined;
  createEffect(() => {
    const here = zonesHere();
    if (!here || !(parking().zones ?? []).length) return;
    const entered = zonesEntered(lastHere, here);
    lastHere = here;
    if (entered[0]) setZoneToast(entered[0]);
  });
  createEffect(() => {
    if (!zoneToast()) return;
    const t = setTimeout(() => setZoneToast(undefined), 5000);
    onCleanup(() => clearTimeout(t));
  });
  const nearby = createMemo(() => {
    const b = bike();
    return b ? nearestParkings(b, parking().parking, 3) : [];
  });
  /** Distance from the bike to a parking, by id. */
  const near = (id: string | undefined) => {
    const b = bike();
    const zone = parking().parking.find((z) => z.id === id);
    return b && zone ? { zone, meters: distanceMeters(b, zone) } : undefined;
  };
  // Following an itinerary (C7): the ride is guided to its parking from the start.
  const followed = () => tripPlan.followedFor(trip()?.vehicleId);
  let targetedPlan = false;
  createEffect(() => {
    const p = followed();
    if (targetedPlan || !p || !parking().parking.some((z) => z.id === p.parking.id)) return;
    targetedPlan = true;
    setTarget(p.parking.id);
  });
  // In a zone again: the guidance target has done its job.
  createEffect(() => {
    if (status().kind === "inside") setTarget(undefined);
  });

  /** Route and callout on the map: E3 / chosen target (navy name), else the nearest parking (06). */
  const route = createMemo(() => {
    if (!trip() || unlockedSheet() || paused()) return undefined;
    const chosen = near(guide() ?? target());
    if (chosen)
      return {
        to: chosen,
        callout: {
          label: `${shortName(chosen.zone.name)} · ${rideDistanceLabel(chosen.meters).split(" · ")[1]}`,
          color: "#164194",
          icon: "/icons/parking-navy-18.svg",
          // Below the parking (Figma E3), unless the bike is south of it.
          placement: (bike()?.lat ?? 0) < chosen.zone.lat ? ("above" as const) : ("below" as const),
        },
      };
    // E1: outside a zone the sheet shows the red band and the map no route (Figma).
    const k = status().kind;
    const first = nearby()[0];
    if (!first || k === "outside" || k === "forbidden") return undefined;
    return {
      to: first,
      callout: {
        label: `Stationnement · ${rideDistanceLabel(first.meters).split(" · ")[1]}`,
        color: "#321ed3",
        icon: "/icons/parking-icon-blue.svg",
        // Above the parking (Figma), unless the bike is north of it: never cover the bike.
        placement: (bike()?.lat ?? 0) > first.zone.lat ? ("below" as const) : ("above" as const),
      },
    };
  });

  createEffect(() => view()?.setParking(parking()));
  createEffect(() => view()?.setPaused(paused()));
  // E2's mini-map route to the nearest parking.
  const oopsPath = createRoutePath(
    () => (oops() ? bike() : undefined),
    () => (oops() ? nearby()[0]?.zone : undefined),
    "bicycle",
  );
  // The ride to that parking along the streets (Pony's direction API), kept as the bike moves.
  const ridePath = createRoutePath(
    () => (route() ? bike() : undefined),
    () => route()?.to.zone,
    "bicycle",
  );
  createEffect(() => {
    const v = view();
    const r = route();
    if (!v) return;
    v.setRoute(ridePath());
    v.setCallout(r ? { at: r.to.zone, ...r.callout } : undefined);
  });

  createEffect(() => {
    const t = trip();
    const v = view();
    if (!t || !v) return;
    const b = bike();
    // Without GBFS (bike in a trip) the bike pin follows the phone: no separate rider dot.
    v.setUser(t.vehicle && geo.located() ? geo.position() : undefined);
    if (b) {
      v.setVehicles([
        {
          id: t.vehicleId,
          vehicleId: t.vehicleId,
          type: "ebike",
          lat: b.lat,
          lng: b.lng,
          battery: t.vehicle?.battery ?? 0,
          rangeMeters: (t.rangeKm ?? 0) * 1000,
        },
      ]);
      v.setSelected({ kind: "vehicle", id: t.vehicleId });
    }
  });

  // Frame the bike (and the parking it is routed to) in the part of the map above the sheet. Not on
  // every GPS fix: the rider can pan; the locate button frames again.
  const frame = () => {
    const v = view();
    const b = bike();
    const r = route();
    if (!v || !b) return;
    const h = container.clientHeight || 800;
    // What the sheet really covers: less once it is collapsed (pushed down, see Sheet).
    const sheet = document.querySelector<HTMLElement>("[data-ride-sheet]")?.parentElement;
    const sheetH = sheet
      ? Math.max(0, container.getBoundingClientRect().bottom - sheet.getBoundingClientRect().top)
      : h / 2;
    const padding = {
      top: r?.callout.placement === "above" ? 120 : 90,
      right: 50,
      bottom: Math.min(sheetH + (r?.callout.placement === "below" ? 70 : 40), h - 200),
      left: 50,
    };
    const pts = r ? [b, r.to.zone] : [b];
    const lngs = pts.map((p) => p.lng);
    const lats = pts.map((p) => p.lat);
    v.fitBounds(
      [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ],
      padding,
    );
  };
  // Not on every GPS fix (the route object is rebuilt as the bike moves): see rideFrameKey.
  const frameKey = createMemo(() =>
    rideFrameKey({
      hasView: !!view(),
      hasBike: !!bike(),
      // A chosen parking only: the nearest one changes as the rider moves.
      routeTo: target(),
      guide: guide(),
      sheet: unlockedSheet() ? "start" : infoSheet() ? "info" : "ride",
      paused: paused(),
    }),
  );
  createEffect(
    on(frameKey, () =>
      // after the sheet for the new state is laid out, its height sets the bottom padding
      setTimeout(frame, 0),
    ),
  );
  /** Figma tam/map-button "Locate" (285:14455): a fresh fix, then the bike and its route again. */
  const recentre = async () => {
    await freshPosition(3_000);
    frame();
  };
  const locateButton = () => (
    <button
      type="button"
      class="absolute right-[11px] bottom-3 flex h-[50px] w-[50px] items-center justify-center rounded-full border border-black bg-white"
      onClick={() => void recentre()}
      aria-label="Recentrer la carte"
    >
      <img src="/icons/locate.svg" width="30" height="30" alt="" />
    </button>
  );

  const position = () => geo.position();

  const failure = (e: unknown, fallback: string) => {
    const f = rideFailure(e, fallback);
    if (f.notice) setNotice({ kind: "error", ...f.notice });
    if (f.refetch) void refetch();
  };

  const pauseResume = async () => {
    const t = trip();
    if (!t) return;
    const action = t.state === "PAUSED" ? "resume" : "pause";
    setBusy(action);
    setNotice(undefined);
    try {
      // A fresh fix: after a pause the phone was often locked, the last one may be stale. Pony waits
      // for the bike's lock or unlock (up to ~90 s, the BFF allows 100 s).
      const at = await freshPosition(3_000);
      mutate(await api.post<TripView>(`/trips/${t.id}/${action}`, { position: at }, 110_000));
      void refetch(); // supersedes a poll started before the action, which would show the old state
      setAskPause(false);
    } catch (e) {
      failure(
        e,
        action === "pause" ? "Impossible de mettre en pause." : "Impossible de reprendre.",
      );
    } finally {
      setBusy(undefined);
    }
  };

  /** Parking photo step (Figma 07/07b) between the end check and FINISH. */
  const [photoStep, setPhotoStep] = createSignal(false);
  const askPhoto = () => {
    setWarning(undefined);
    setBusy(undefined);
    setPhotoStep(true);
  };

  const finish = async (photo?: Blob) => {
    const t = trip();
    if (!t) return;
    setBusy("end");
    setNotice(undefined);
    setWarning(undefined);
    // PoC: the photo is taken and validated but not uploaded (the server route /api/trips/:id/photo
    // exists for later); wait 1 s like an upload, then end the ride.
    if (photo) await new Promise((r) => setTimeout(r, 1000));
    try {
      const summary = await api.post<TripSummary>(
        `/trips/${t.id}/finish`,
        { position: position() },
        110_000,
      );
      navigate(`/trip/${summary.id}`, { replace: true });
    } catch (e) {
      failure(e, "Impossible de terminer le trajet. Réessayez.");
      setPhotoStep(false);
      setBusy(undefined);
    }
  };

  const checkEnd = async () => {
    const t = trip();
    if (!t) return;
    setBusy("end");
    setNotice(undefined);
    try {
      const check = await api.post<EndCheck>(`/trips/${t.id}/check-end`, { position: position() });
      if (check.warning) {
        // Outside a zone with a parking nearby: E2 shows it first; otherwise E4 straight away.
        if (check.verdict === "NOT_IN_PARKING_ZONE" && nearby()[0] && !paused()) setOops(check);
        else setWarning(check);
        setBusy(undefined);
      } else askPhoto();
    } catch (e) {
      if (e instanceof ApiFailure && e.code in REFUSALS)
        setNotice({ kind: "refusal", text: e.userMessage ?? REFUSALS[e.code] ?? "" });
      else failure(e, "Vérification impossible. Réessayez.");
      setBusy(undefined);
    }
  };

  /** E4 "Retour": back to the ride, on the guidance (E3) when there is a parking to go to. */
  const backFromProof = () => {
    setWarning(undefined);
    const first = nearby()[0];
    // Guidance only while riding: from P2 the rider stays on the paused sheet.
    if (first && !paused()) setGuide(first.zone.id);
  };

  const noticeBox = () => (
    <Show when={notice()}>
      {(n) => (
        <div
          class={`mb-3 rounded-[10px] px-3 py-2 text-sm leading-[1.25] ${n().kind === "refusal" ? "bg-[#fcebeb] text-[#c62828]" : n().kind === "error" ? "bg-amber-50 text-amber-900" : "bg-surface2 text-violet"}`}
          role="alert"
        >
          {n().text}
        </div>
      )}
    </Show>
  );

  return (
    <main class="screen-fixed bg-surface">
      <div class="absolute inset-0">
        <div ref={container} class="h-full w-full" />
      </div>
      <button
        type="button"
        class="absolute right-[11px] h-[50px] w-[50px] rounded-full bg-violet text-white shadow flex items-center justify-center z-10"
        style={{ top: MAP_TOP }}
        onClick={() => setMenu(true)}
        aria-label="Menu"
      >
        <MenuIcon />
      </button>
      <MenuDrawer open={menu()} onClose={() => setMenu(false)} />

      <MapTopRow>
        {/* Entering a zone (decision 14/10: a toast, K2–K5 titles). */}
        <Show when={zoneToast()} keyed>
          {(z) => <ZoneToast zone={z} onClose={() => setZoneToast(undefined)} />}
        </Show>
        {/* Figma C7 / C8: the followed itinerary's step (ride to its parking). */}
        <Show when={!unlockedSheet() && followed()}>
          {(p) => <ItineraryBanner {...bannerFor(p(), "riding")} />}
        </Show>
        <Show when={trip.state === "pending"}>
          <div class="flex justify-center">
            <LoadingPill label="Chargement du trajet…" />
          </div>
        </Show>
      </MapTopRow>

      {/* Figma P2 "En pause" map pill, 64 px under the status bar. */}
      <Show when={paused() && !unlockedSheet()}>
        <div
          class="pointer-events-none absolute inset-x-0 z-10 flex justify-center"
          style={{ top: "calc(max(1rem, env(safe-area-inset-top)) + 64px)" }}
          role="status"
        >
          <span class="flex h-10 w-40 items-center gap-[7px] rounded-[20px] bg-violet pl-[18px] text-base font-semibold leading-[1.25] tracking-[0.16px] text-white shadow-[0_2px_6px_rgba(0,0,0,0.25)]">
            <img src="/icons/pause-white-20.svg" width="20" height="20" alt="" />
            En pause
          </span>
        </div>
      </Show>

      <Show when={trip()}>
        {(t) => (
          <>
            {/* ---- 05b Départ (52:9309) / 06b Infos du trajet (285:14509) ---- */}
            <Show when={unlockedSheet() || infoSheet()}>
              <Sheet
                above={infoSheet() ? locateButton() : undefined}
                collapsible
                onCollapsedChange={() => frame()}
              >
                <div data-ride-sheet class="contents" />
                <RideInfo
                  trip={t()}
                  paused={paused()}
                  holdMethod={holdMethodLabel(method() ?? undefined)}
                  duo={duo()}
                  onDuo={() => setDuoChoice(true)}
                  onStories={setStories}
                />
                <Button
                  class="mt-6 -mx-[5px] !w-[calc(100%+10px)]"
                  onClick={() => (infoSheet() ? setInfoSheet(false) : setUnlockedSheet(false))}
                >
                  {infoSheet() ? "Retour au trajet" : "C'est parti"}
                </Button>
              </Sheet>
            </Show>

            {/* ---- E3 Guidage vers un stationnement ---- */}
            <Show when={unlockedSheet() || infoSheet() ? undefined : near(guide())}>
              {(g) => (
                <Sheet above={locateButton()} collapsible onCollapsedChange={() => frame()}>
                  <div data-ride-sheet class="contents" />
                  <div data-sheet-peek class="flex items-start gap-3">
                    <span class="relative mt-[2px] h-[42px] w-[42px] shrink-0">
                      <img src="/icons/parking-circle-navy.svg" width="42" height="42" alt="" />
                      <img
                        src="/icons/parking-navy-24.svg"
                        width="24"
                        height="24"
                        alt=""
                        class="absolute left-[9px] top-[9px]"
                      />
                    </span>
                    <div class="min-w-0">
                      <h2 class="text-xl font-semibold leading-[1.25] tracking-[0.2px] text-strong">
                        {g().zone.name}
                      </h2>
                      <p class="mt-[3px] text-[13px] leading-[1.25] tracking-[0.13px] text-text">
                        {rideDistanceLabel(g().meters)} à vélo
                      </p>
                    </div>
                  </div>
                  <Show when={nearby().filter((n) => n.zone.id !== g().zone.id).length > 0}>
                    <h3 class="mt-5 text-[13px] font-medium leading-[1.25] tracking-[0.13px] text-strong">
                      Stationnements à proximité
                    </h3>
                    <div class="mt-[6px] -mx-[5px] grid grid-cols-2 gap-3">
                      <For
                        each={nearby()
                          .filter((n) => n.zone.id !== g().zone.id)
                          .slice(0, 2)}
                      >
                        {(n) => (
                          <button
                            type="button"
                            class="flex h-14 items-center gap-2 rounded-[10px] bg-surface2 pl-3 pr-2 text-left active:brightness-95"
                            onClick={() => setGuide(n.zone.id)}
                          >
                            <img
                              src="/icons/parking-navy-22.svg"
                              width="22"
                              height="22"
                              alt=""
                              class="shrink-0"
                            />
                            <span class="min-w-0">
                              <span class="block truncate text-sm font-medium leading-[1.25] tracking-[0.14px] text-strong">
                                {shortName(n.zone.name)}
                              </span>
                              <span class="block text-xs leading-[1.25] tracking-[0.12px] text-text">
                                {rideDistanceLabel(n.meters)}
                              </span>
                            </span>
                          </button>
                        )}
                      </For>
                    </div>
                  </Show>
                  <div class="-mx-[5px]">
                    <Button
                      class="mt-[22px]"
                      onClick={() => {
                        setTarget(g().zone.id);
                        setGuide(undefined);
                      }}
                    >
                      Y aller
                    </Button>
                    <Button variant="outline" class="mt-[10px]" onClick={() => comingSoon()}>
                      Suggérer une zone de stationnement
                    </Button>
                  </div>
                  <OperatedBy />
                </Sheet>
              )}
            </Show>

            {/* ---- P2 Trajet en pause ---- */}
            <Show when={!unlockedSheet() && !infoSheet() && !guide() && paused()}>
              <Sheet
                handle="overlay"
                above={locateButton()}
                collapsible
                onCollapsedChange={() => frame()}
              >
                <div data-ride-sheet class="contents" />
                <div class="relative -mx-5 -mt-[10px] h-[72px] rounded-t-[24px] bg-violet text-white">
                  <div class="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-[2px] bg-handle opacity-70" />
                  <img
                    src="/icons/pause-white-26.svg"
                    width="26"
                    height="26"
                    alt=""
                    class="absolute left-5 top-6"
                  />
                  <div class="absolute left-[56px] right-5 top-5">
                    <div class="text-[19px] font-semibold leading-[1.25] tracking-[0.19px]">
                      Trajet en pause
                    </div>
                    <div class="text-xs leading-[1.25] tracking-[0.12px] opacity-90">
                      Votre vélo est verrouillé et reste réservé pour vous
                    </div>
                  </div>
                </div>
                <div class="mt-[18px]">{noticeBox()}</div>
                <VehicleHeader id={t().vehicleId} />
                {/* Collapsed, the paused sheet keeps its header, the vehicle and these stats. */}
                <div data-sheet-peek class="mt-[18px] grid grid-cols-[124px_1fr]">
                  <RideStat icon="/icons/clock.svg" value={mmss(elapsed())} />
                  <RideStat icon="/icons/battery-violet.svg" value={rangeLabel(t())} />
                </div>
                <p class="mt-4 text-xs leading-[1.25] tracking-[0.12px] text-text">
                  La tarification continue pendant la pause (
                  {rateOf(t().priceLabel)?.text ?? t().priceLabel}).
                </p>
                <div class="mt-[10px] border-t border-border" />
                <div class="-mx-[5px] mt-4">
                  <SlideToConfirm
                    label="Glissez pour reprendre"
                    onConfirm={() => void pauseResume()}
                    disabled={busy() !== undefined || !!warning() || !!oops()}
                    loading={busy() === "resume"}
                  />
                  <Button
                    variant="outline"
                    class="mt-4 mb-[30px]"
                    loading={busy() === "end"}
                    disabled={busy() !== undefined || !!warning() || !!oops()}
                    onClick={() => void checkEnd()}
                  >
                    Terminer le trajet
                  </Button>
                </div>
              </Sheet>
            </Show>

            {/* ---- 06 Trajet en cours / E1 hors zone ---- */}
            <Show when={!unlockedSheet() && !infoSheet() && !guide() && !paused()}>
              <Sheet above={locateButton()} collapsible onCollapsedChange={() => frame()}>
                <div data-ride-sheet class="contents" />
                {noticeBox()}
                <Show when={status().kind === "inside"}>
                  <SheetBand tone="ok">Vous pouvez terminer votre trajet ici</SheetBand>
                </Show>
                <Show when={status().kind === "outside" || status().kind === "forbidden"}>
                  <SheetBand
                    tone="warn"
                    {...(nearby().length > 0 && {
                      onClick: () => setGuide(nearby()[0]?.zone.id),
                    })}
                  >
                    Vous ne pouvez pas stationner ici. Trouvez un stationnement sur la carte.
                  </SheetBand>
                </Show>
                {/* Figma 06: the whole 70 px vehicle row opens 06b; the link only labels it. */}
                <button
                  type="button"
                  class={`-mx-5 block w-[calc(100%+40px)] px-5 text-left active:bg-surface2 ${status().kind === "unknown" && !notice() ? "" : "mt-5"}`}
                  onClick={() => setInfoSheet(true)}
                >
                  <span class="flex items-center justify-between">
                    <VehicleHeader id={t().vehicleId} />
                    <span class="mt-[10px] text-sm leading-[1.25] tracking-[0.14px] text-link underline">
                      Infos du trajet
                    </span>
                  </span>
                </button>
                {/* Collapsed, the sheet keeps the band, the vehicle and these stats. */}
                <div data-sheet-peek class="mt-5 grid grid-cols-[124px_124px_1fr]">
                  <RideStat icon="/icons/clock.svg" value={mmss(elapsed())} />
                  <RideStat icon="/icons/battery-violet.svg" value={rangeLabel(t())} />
                  <RideStat icon="/icons/speed.svg" value={`${speedNow()} km/h`} />
                </div>
                <div class="mt-4 border-t border-border" />
                <div class="-mx-[5px] mt-[13px]">
                  <SlideToConfirm
                    label="Glissez pour terminer"
                    onConfirm={() => void checkEnd()}
                    disabled={busy() !== undefined || !!warning() || !!oops() || askPause()}
                    loading={busy() === "end"}
                  />
                  <div class="mt-[18px] grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      disabled={busy() !== undefined}
                      onClick={() => {
                        setNotice(undefined);
                        setAskPause(true);
                      }}
                    >
                      Pause
                    </Button>
                    <Button variant="outline" disabled>
                      Ajouter un vélo
                    </Button>
                  </div>
                </div>
                <div class="mt-[10px]">
                  <ListRow label="Zones et stationnement" onClick={() => setStories("zones")} />
                  <ListRow
                    label="Aide"
                    onClick={() =>
                      navigate(reportHref({ vehicle: t().vehicleId, trip: t().id, back: "/ride" }))
                    }
                  />
                </div>
              </Sheet>
            </Show>
          </>
        )}
      </Show>

      {/* ---- P1 Mettre en pause ---- */}
      <Show when={askPause() && trip() && !paused()}>
        <Modal
          label="Mettre en pause"
          onClose={() => setAskPause(false)}
          header={
            <DiscWithBadge
              disc="/icons/pause-circle-violet.svg"
              glyph="/icons/pause-white-44.svg"
              badge="/icons/lock-badge-circle.svg"
              badgeSize={32}
              badgeGlyph="/icons/lock-violet-18.svg"
            />
          }
        >
          <h2 class="modal-title">Mettre en pause</h2>
          <p class="modal-text mt-[10px] px-1">
            Votre vélo est verrouillé et reste réservé pour vous. La tarification continue pendant
            la pause.
          </p>
          <Show when={notice()}>
            {(n) => (
              <p class="mt-2 text-sm text-red-700" role="alert">
                {n().text}
              </p>
            )}
          </Show>
          <div class="mt-6">
            <SlideToConfirm
              label="Glissez pour mettre en pause"
              onConfirm={() => void pauseResume()}
              loading={busy() === "pause"}
            />
          </div>
          <Button variant="outline" class="mt-3" onClick={() => setAskPause(false)}>
            Retour
          </Button>
        </Modal>
      </Show>

      {/* ---- E2 Pas dans une zone (no free minutes: it points to the nearest parking) ---- */}
      <Show when={oops() && bike() && nearby()[0]}>
        {(n) => (
          <ParkingCheckModal
            bike={bike() as Position}
            zone={n().zone}
            path={oopsPath() ?? [bike() as Position, n().zone]}
            distance={rideDistanceLabel(n().meters).split(" · ")[0] ?? ""}
            onClose={() => setOops(undefined)}
            onFind={() => {
              setOops(undefined);
              setGuide(n().zone.id);
            }}
            onParked={() => {
              setWarning(oops());
              setOops(undefined);
            }}
          />
        )}
      </Show>

      {/* ---- E4 Preuve de stationnement ---- */}
      <Show when={warning()}>
        {(w) => (
          <Modal
            label="Attention"
            onClose={backFromProof}
            header={
              <DiscWithBadge
                disc="/icons/circle-white-88.svg"
                glyph="/icons/warning-red-44.svg"
                badge="/icons/camera-badge-circle.svg"
                badgeSize={34}
                badgeGlyph="/icons/camera-white-18.svg"
              />
            }
          >
            <h2 class="modal-title">Attention</h2>
            <p class="modal-text mt-[10px] px-1">
              {w().verdict === "NOT_IN_PARKING_ZONE" ? PROOF_TEXT : w().warning}
            </p>
            <Button class="mt-6" onClick={askPhoto}>
              Prendre une photo
            </Button>
            <Button variant="outline" class="mt-3" onClick={backFromProof}>
              Retour
            </Button>
          </Modal>
        )}
      </Show>

      <Show when={photoStep()}>
        <ParkingPhoto
          busy={busy() === "end"}
          onBack={() => setPhotoStep(false)}
          onDone={(photo) => void finish(photo)}
        />
      </Show>
      <Show when={zoneSheet()}>
        {(z) => <ZoneSheet zone={z()} onClose={() => setZoneSheet(undefined)} />}
      </Show>
      <Show when={duoChoice()}>
        <DuoChoiceSheet
          value={duo() ? "duo" : "solo"}
          onChoose={chooseMode}
          onClose={() => setDuoChoice(false)}
        />
      </Show>
      {/* Keyed: another set remounts the player from its first card. */}
      <Show when={stories()} keyed>
        {(set) => (
          <Stories
            set={set}
            onClose={() => setStories(undefined)}
            onDone={() => {
              markStorySeen(set);
              setStories(undefined);
            }}
          />
        )}
      </Show>
      <ComingSoonToast />
    </main>
  );
}

const rangeLabel = (t: TripView) =>
  t.rangeKm !== undefined
    ? `${t.rangeKm} km`
    : t.vehicle
      ? `${Math.round(t.vehicle.rangeMeters / 1000)} km`
      : "—";

/** Figma vehicle header (06, P2): 74 × 62 photo, "VÉLO ÉLECTRIQUE" overline, id. Spans: it sits in a button. */
function VehicleHeader(props: { id: string; class?: string }) {
  return (
    <span class={`-ml-[2px] flex items-center gap-3 ${props.class ?? ""}`}>
      <img
        src="/img/ebike.webp"
        width="74"
        height="62"
        alt=""
        class="h-[62px] w-[74px] object-contain"
      />
      <span class="-mt-[10px] block">
        <span class="block text-[11px] font-semibold uppercase leading-[1.25] tracking-[0.11px] text-violet">
          {props.id.toUpperCase().startsWith("S") ? "Trottinette électrique" : "Vélo électrique"}
        </span>
        <span class="mt-1 block text-[22px] font-semibold leading-[1.25] tracking-[0.22px] text-strong">
          {props.id}
        </span>
      </span>
    </span>
  );
}

/**
 * Figma 05b (52:9309), reused by 06b (285:14509): the vehicle block of the vehicle sheet with an
 * "En trajet" chip, both fares in a lilac pill (public from GBFS, Tarif bleu hardcoded), the hold
 * on the rider's payment method, then Mode duo (display only) and the zones / tuto rows.
 */
function RideInfo(props: {
  trip: TripView;
  paused: boolean;
  holdMethod: string;
  duo: boolean;
  /** The toggle opens the solo / duo choice (U1/U2). */
  onDuo: () => void;
  onStories: (set: StorySet) => void;
}) {
  const scooter = () => props.trip.vehicleId.toUpperCase().startsWith("S");
  return (
    <>
      {/* Collapsed, the 05b / 06b sheet keeps the vehicle block ("En trajet", id). */}
      <div data-sheet-peek class="flex items-center gap-4">
        <img
          src="/img/ebike.webp"
          width="92"
          height="78"
          alt=""
          class="h-[78px] w-[92px] shrink-0 object-contain"
        />
        <div class="min-w-0">
          <span class="inline-flex h-5 items-center rounded-[10px] bg-violet px-[10px] text-[11px] font-medium leading-[1.25] tracking-[0.11px] text-white">
            {props.paused ? "En pause" : "En trajet"}
          </span>
          <div class="mt-[6px] text-[22px] font-semibold leading-[1.25] tracking-[0.22px] text-strong">
            {props.trip.vehicleId}
          </div>
          <div class="mt-[2px] text-[13px] leading-[1.25] tracking-[0.13px] text-text">
            {scooter() ? "Trottinette électrique" : "Vélo électrique 2 places"}
          </div>
        </div>
      </div>
      <div class="mt-4 rounded-[8px] bg-surface2 px-3 py-2 text-center text-sm font-medium leading-[1.25] tracking-[0.14px] text-violet">
        <div>Tous publics : {props.trip.priceLabel}</div>
        <div class="mt-[2px]">{TARIF_BLEU_LABEL}</div>
      </div>
      <p class="mt-2 text-center text-xs leading-[1.25] tracking-[0.12px] text-text">
        Empreinte de {euros(props.trip.holdCents)} réservée sur {props.holdMethod}, ajustée à la fin
        du trajet
      </p>
      <div class="mt-[14px] border-t border-border" />
      <div class="flex items-start justify-between gap-3 border-b border-border pt-[13px] pb-[9px]">
        <div>
          <div class="text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-strong">
            {props.duo ? "Mode duo activé" : "Mode duo"}
          </div>
          <div class="mt-[1px] text-xs leading-[1.25] tracking-[0.12px] text-text">
            Démarrer plus facilement avec un passager
          </div>
        </div>
        <Toggle checked={props.duo} onChange={() => props.onDuo()} label="Mode duo" />
      </div>
      <ListRow label="Zones et stationnement" onClick={() => props.onStories("zones")} />
      <ListRow label="Tuto et sécurité" onClick={() => props.onStories("safety")} />
    </>
  );
}

/** Ride stat (06 / P2): bare 22 px icon, 8 px, SemiBold 18 value. */
function RideStat(props: { icon: string; value: string }) {
  return (
    <div class="flex items-center gap-2">
      <img src={props.icon} width="22" height="22" alt="" />
      <span class="text-lg font-semibold leading-[1.25] tracking-[0.18px] text-strong tabular-nums">
        {props.value}
      </span>
    </div>
  );
}

/** Modal illustration (P1, E4): 88 px disc, 44 px glyph, badge on its bottom-right. */
function DiscWithBadge(props: {
  disc: string;
  glyph: string;
  badge: string;
  badgeSize: number;
  badgeGlyph: string;
}) {
  return (
    <span class="relative block h-[88px] w-[88px]">
      <img src={props.disc} width="88" height="88" alt="" />
      <img
        src={props.glyph}
        width="44"
        height="44"
        alt=""
        class="absolute left-[22px] top-[22px]"
      />
      <span
        class="absolute left-[66px] top-[62px]"
        style={{ width: `${props.badgeSize}px`, height: `${props.badgeSize}px` }}
      >
        <img src={props.badge} width={props.badgeSize} height={props.badgeSize} alt="" />
        <img
          src={props.badgeGlyph}
          width="18"
          height="18"
          alt=""
          class="absolute"
          style={{
            left: `${(props.badgeSize - 18) / 2}px`,
            top: `${(props.badgeSize - 18) / 2}px`,
          }}
        />
      </span>
    </span>
  );
}

/** pony/operated-by: "Service opéré par" + the pony wordmark. */
function OperatedBy() {
  return (
    <div class="mt-5 flex items-center justify-center gap-[5px] text-xs leading-[1.25] tracking-[0.12px] text-text">
      Service opéré par
      <img src="/icons/pony-logotype-indigo.svg" width="31" height="15" alt="pony" />
    </div>
  );
}
